/**
 * Decides, across every open Atlas tab, whether a message should alert and which
 * tab does it.
 *
 * Every tab holds its own socket and receives the same CHANNEL_MESSAGE_CREATED, so
 * something has to arbitrate or three tabs chime three times. The first attempt let
 * each tab judge from its own visibility and its own open channel and then race for
 * a lock — which got both halves wrong. A hidden tab could win and alert for a
 * message the user was reading in a visible tab, and *which* effects fired at all
 * depended on who won the race, so the same situation behaved differently run to
 * run. "Is the user looking at this conversation" is a question about the user, and
 * no single tab can answer it.
 *
 * So the tabs pool what they know before anyone acts: each broadcasts its own view,
 * waits briefly for its peers, and then every tab computes the same verdict from the
 * same set. The winner decides only *who* performs the alert, never *what* happens.
 *
 * Two properties make the pooling symmetric rather than a second race:
 *
 * 1. **Peer views are buffered, not dropped.** A tab that hears a peer before its
 *    own socket has delivered the message keeps that view (see `recordView`), so
 *    which tab's socket fired first stops mattering.
 * 2. **Every announcement is answered.** Hearing a peer makes this tab broadcast its
 *    own view even when it has no round of its own, so a tab whose socket lags — or
 *    never delivers at all — still contributes its visibility to everyone's verdict.
 *    This is what the suppression check depends on, and it is reliable for the tabs
 *    that matter: only a *visible* tab can suppress an alert, and a visible tab is
 *    neither frozen nor timer-throttled, so its answer lands inside the window.
 *
 * Electing the executor is a separate problem, because tabs resolve their rounds at
 * different moments and so cannot be trusted to have identical sets at that instant.
 * It is settled by claiming rather than by agreement, so a tab arriving late finds the
 * message taken instead of reaching a second, equally reasonable conclusion. The claim
 * takes two pieces, because neither works alone: a Web Lock to serialise it against
 * the other tabs, and a record in localStorage of what has already alerted. A lock
 * cannot outlive the tab holding it and so says nothing to a tab that wakes up an hour
 * later; storage is durable but has no compare-and-swap. Writing the record while
 * holding the lock gives both.
 *
 * Where the record cannot be written at all — private mode, a partitioned context,
 * an exhausted quota — the pair does not fall back to the lock, it falls back to
 * nothing: the lock is released the moment the write would have happened, so it never
 * overlaps two tabs claiming a moment apart. Claiming is therefore abandoned in that
 * case in favour of agreement (`electExecutor`), which needs no storage.
 */

const BROADCAST_CHANNEL_NAME = 'atlas-message-alerts'

const EXECUTOR_LOCK_PREFIX = 'atlas-message-alert-'

/**
 * How long to wait for peers. Long enough for a same-origin BroadcastChannel
 * round-trip (sub-millisecond in practice), short enough to be imperceptible on a
 * notification.
 */
const PEER_WINDOW_MS = 80

/** Where the tabs record what has already alerted. Shared by all of them. */
const ALERTED_STORAGE_KEY = 'atlas-alerted-messages'

/** Written and immediately removed, only to find out whether writing is possible. */
const STORAGE_PROBE_KEY = 'atlas-alerted-probe'

/**
 * How long a message stays on that record. It has to outlast tab suspension, which
 * is the case a lock cannot cover — a tab frozen mid-round can resume minutes later
 * and would otherwise replay the sound and the banner for a message everyone else
 * finished with. Past this, a message is old enough that alerting again is the
 * lesser problem, and the record is pruned so it can't grow without bound.
 */
const ALERTED_TTL_MS = 10 * 60_000

/**
 * How long a peer's view is kept for a message this tab has not been handed yet.
 * Long enough to survive a lagging socket, short enough that a long-lived tab isn't
 * accumulating rounds nobody will finish.
 */
const BUFFERED_VIEW_TTL_MS = 5_000

/** One tab's answer to "what are you looking at right now?". */
export interface TabView {
  tabId: string
  /** The conversation this tab has open, if any. */
  activeChannelId: string | null
  /**
   * Conversations open beside it in split view. Optional for the same reason as
   * `isFocused`: a tab on the previous build broadcasts a view without it.
   */
  splitChannelIds?: string[]
  isVisible: boolean
  /**
   * Optional because a tab still running the previous build broadcasts a view
   * without it, and those are read as focused so their suppression behaves as it
   * did before rather than double-alerting through a deploy.
   */
  isFocused?: boolean
}

type LocalView = Omit<TabView, 'tabId'>

interface AlertRoundMessage {
  messageId: string
  view: TabView
}

export interface AlertVerdict {
  /**
   * True when some visible, focused tab has this conversation on screen.
   *
   * The reader is already looking at the message, so it earns the in-app toast
   * and nothing else: a sound and an OS banner for something in front of you are
   * interruptions announcing what you are in the middle of reading.
   */
  isOnScreen: boolean
  /**
   * True only in the single tab elected to perform the once-per-message effects —
   * the sound and the OS banner. Not used for the in-app toast, which has to render
   * wherever the user is actually looking rather than wherever the election landed.
   */
  isExecutor: boolean
}

const isAlertRoundMessage = (data: unknown): data is AlertRoundMessage => {
  if (!data || typeof data !== 'object') return false
  const candidate = data as Partial<AlertRoundMessage>
  return typeof candidate.messageId === 'string' && !!candidate.view
}

/**
 * Stable for the life of the document. Only used to identify a view's author and,
 * where Web Locks are missing, to break the executor tie — so it has to be unique,
 * not ordered.
 */
const tabId =
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`

export const getTabId = (): string => tabId

const broadcast =
  typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(BROADCAST_CHANNEL_NAME) : null

/**
 * What this tab is looking at, asked at the moment a peer wants to know. Supplied by
 * the middleware, which is the only thing here with a view of the store; the default
 * keeps the module usable (and testable) on its own.
 */
let readLocalView: () => LocalView = () => ({
  activeChannelId: null,
  isVisible: typeof document !== 'undefined' && document.visibilityState === 'visible',
  isFocused: typeof document !== 'undefined' && document.hasFocus(),
})

export const setLocalViewProvider = (provider: () => LocalView): void => {
  readLocalView = provider
}

/** Views collected per message id, keyed by tab, including this tab's own. */
const rounds = new Map<string, Map<string, TabView>>()

/** Message ids this tab has already answered, so two tabs don't answer each other forever. */
const answered = new Set<string>()

const expiryTimers = new Map<string, ReturnType<typeof setTimeout>>()

const forgetRound = (messageId: string): void => {
  rounds.delete(messageId)
  answered.delete(messageId)
  const timer = expiryTimers.get(messageId)
  if (timer) {
    clearTimeout(timer)
    expiryTimers.delete(messageId)
  }
}

/**
 * Record a view against a message id, starting the round if this is the first thing
 * heard about it. Buffering a peer we hear about before our own socket delivers is
 * the whole point — dropping it is what let two tabs decide from different sets.
 */
const recordView = (messageId: string, view: TabView): void => {
  const collected = rounds.get(messageId) ?? new Map<string, TabView>()
  collected.set(view.tabId, view)
  rounds.set(messageId, collected)

  // A round this tab resolves is cleared on resolution; this only catches the ones
  // it never gets handed, so nothing accumulates for the life of the document.
  if (!expiryTimers.has(messageId)) {
    expiryTimers.set(
      messageId,
      setTimeout(() => forgetRound(messageId), BUFFERED_VIEW_TTL_MS),
    )
  }
}

const announce = (messageId: string, view: TabView): void => {
  broadcast?.postMessage({ messageId, view })
}

broadcast?.addEventListener('message', (event: MessageEvent) => {
  if (!isAlertRoundMessage(event.data)) return
  const { messageId, view } = event.data
  if (view.tabId === tabId) return

  recordView(messageId, view)

  // Answer once, so the peer decides from a set that includes this tab even if this
  // tab's own socket is behind. Answering more than once would ping-pong.
  if (answered.has(messageId)) return
  answered.add(messageId)
  announce(messageId, { ...readLocalView(), tabId })
})

const supportsWebLocks = typeof navigator !== 'undefined' && 'locks' in navigator

type AlertedMessages = Record<string, number>

const readAlertedMessages = (): AlertedMessages => {
  try {
    const stored = localStorage.getItem(ALERTED_STORAGE_KEY)
    if (!stored) return {}
    const parsed: unknown = JSON.parse(stored)
    if (!parsed || typeof parsed !== 'object') return {}
    return parsed as AlertedMessages
  } catch {
    // Unavailable in private mode, and unparseable if something else wrote here.
    // Either way the claim falls back to the lock alone.
    return {}
  }
}

/**
 * Whether the durable record can be written at all.
 *
 * Probed rather than assumed, because the lock is worthless without it. The lock is
 * released the instant the record is written, so it never overlaps two tabs that
 * claim a moment apart — the record is the only thing standing between them. Where
 * writing is impossible the pair does not degrade to "the lock alone", it degrades
 * to nothing, and every tab claims the same message.
 *
 * Cached: the dominant failure is storage being blocked for the whole document
 * (private mode, partitioned or third-party contexts), which does not change while
 * that document is open. A write that fails later clears it back to false.
 */
let canPersistClaims: boolean | null = null

const probeClaimStorage = (): boolean => {
  if (canPersistClaims !== null) return canPersistClaims

  try {
    localStorage.setItem(STORAGE_PROBE_KEY, '1')
    localStorage.removeItem(STORAGE_PROBE_KEY)
    canPersistClaims = true
  } catch {
    canPersistClaims = false
  }
  return canPersistClaims
}

/**
 * `unarbitrated` is the case that has to be named rather than folded into either
 * side: the record could not be written, so this tab's claim is invisible to every
 * other tab — not merely to one resuming later. Reporting it as a win is what let
 * all of them win.
 */
type ClaimOutcome = 'claimed' | 'lost' | 'unarbitrated'

/**
 * Take the claim if nobody has, recording it so a tab that resumes later can see it
 * was taken. Pruning on write keeps the record to the handful of messages that could
 * still have a round in flight somewhere.
 *
 * Synchronous on purpose: the read and the write have to happen inside the lock, so
 * nothing may be awaited between them.
 */
const takeClaim = (messageId: string): ClaimOutcome => {
  const alerted = readAlertedMessages()
  if (alerted[messageId]) return 'lost'

  const now = Date.now()
  const cutoff = now - ALERTED_TTL_MS
  const pruned: AlertedMessages = { [messageId]: now }
  Object.entries(alerted).forEach(([id, alertedAt]) => {
    if (alertedAt > cutoff) pruned[id] = alertedAt
  })

  try {
    localStorage.setItem(ALERTED_STORAGE_KEY, JSON.stringify(pruned))
  } catch {
    // It probed as writable a moment ago, so this is the quota giving out mid-flight.
    // The record is what makes a claim visible to anyone else, so without it there is
    // no claim to report — hand the decision back to the deterministic election, and
    // stop trusting storage for the messages after this one.
    canPersistClaims = false
    return 'unarbitrated'
  }
  return 'claimed'
}

/**
 * Claim the right to perform this message's once-per-message effects.
 *
 * `ifAvailable` makes the lock a test-and-set rather than a queue: a tab that does
 * not get it is told so immediately instead of waiting. The lock is released as soon
 * as the record is written — holding it longer was the previous approach and bought
 * nothing, since a lock dies with its tab and cannot be what a resuming tab checks.
 */
const claimExecutor = (messageId: string): Promise<ClaimOutcome> =>
  new Promise<ClaimOutcome>((resolve) => {
    void navigator.locks
      // Returning nothing releases the lock as soon as the callback does, which is
      // all the time the synchronous read-and-write inside `takeClaim` needs.
      .request(`${EXECUTOR_LOCK_PREFIX}${messageId}`, { ifAvailable: true }, (lock): void => {
        resolve(lock ? takeClaim(messageId) : 'lost')
      })
      .catch(() => resolve('unarbitrated'))
  })

/** Agreement rather than claiming: every tab computes the same winner from the same set. */
const isLowestTabId = (views: TabView[], localTabId: string): boolean =>
  views
    .map((view) => view.tabId)
    .reduce((current, candidate) => (candidate < current ? candidate : current), localTabId) ===
  localTabId

/**
 * Decide whether this tab performs the once-per-message effects.
 *
 * Claiming is preferred because it settles a late arrival correctly — it finds the
 * message taken instead of reaching a second, equally reasonable conclusion. Where
 * claiming cannot work, agreement is the fallback rather than the absence of one:
 * what it loses is only the guard against two tabs deciding from sets that differ at
 * that instant, which is a far smaller hole than every tab alerting.
 */
const electExecutor = async (
  views: TabView[],
  messageId: string,
  localTabId: string,
): Promise<boolean> => {
  if (!probeClaimStorage()) return isLowestTabId(views, localTabId)

  const outcome = supportsWebLocks
    ? await claimExecutor(messageId)
    : // Without Web Locks, only the agreed winner attempts the claim, so the record
      // still catches a tab resuming after everyone else has finished.
      isLowestTabId(views, localTabId)
      ? takeClaim(messageId)
      : 'lost'

  if (outcome === 'unarbitrated') return isLowestTabId(views, localTabId)
  return outcome === 'claimed'
}

const decide = async (
  views: TabView[],
  messageId: string,
  channelId: string,
  localTabId: string,
): Promise<AlertVerdict> => {
  // Somebody has this conversation on screen — the message is already in front of
  // the user. This is the check a single tab could not make. `isVisible` alone is
  // true for a background window on a second monitor, which is not "in front of
  // the user" — and one such tab was silencing the alert for every other tab too.
  //
  // It no longer silences everything: the toast still renders where the reader is
  // looking, because a conversation you are sitting in now stays unread until you
  // leave it, and a message you were never told about is one you can miss while
  // staring at it. Only the sound and the banner are withheld.
  const isOnScreen = views.some(
    (view) =>
      view.isVisible &&
      (view.isFocused ?? true) &&
      (view.activeChannelId === channelId || !!view.splitChannelIds?.includes(channelId)),
  )
  if (isOnScreen) return { isOnScreen, isExecutor: false }

  return { isOnScreen, isExecutor: await electExecutor(views, messageId, localTabId) }
}

/**
 * Pool this tab's view with its peers', then return the verdict every tab agrees on.
 *
 * Resolves on the local view alone where BroadcastChannel is missing — a single tab,
 * which is the common case anyway.
 */
export const resolveAlertAcrossTabs = (
  messageId: string,
  channelId: string,
  localView: LocalView,
): Promise<AlertVerdict> => {
  const view: TabView = { ...localView, tabId }

  if (!broadcast) {
    return decide([view], messageId, channelId, tabId)
  }

  // Merges onto whatever peers have already said rather than replacing it.
  recordView(messageId, view)
  // Marked answered before announcing: this view is what an answer would have said,
  // so a peer's announcement arriving next needs no further reply from here.
  answered.add(messageId)
  announce(messageId, view)

  return new Promise((resolve) => {
    setTimeout(() => {
      const collected = Array.from(rounds.get(messageId)?.values() ?? [view])
      forgetRound(messageId)
      resolve(decide(collected, messageId, channelId, tabId))
    }, PEER_WINDOW_MS)
  })
}
